import CachedDictionary from '../../../../services/engines/CachedDictionary';
import logService from '../../../../services/wss/logService3';

/**
 * Stores node objects per node key.
 * Node keys are dynamic — whatever the group defines (g1, g2, ...).
 * Only GroupNodeEngine should use this cache directly.
 */
class GroupNodeCache extends CachedDictionary {
    _name = 'GroupNodeCache';

    setNode = (key, node) => {
        if (node?.entries?.length) {
            const seen = new Map();
            node.entries.forEach((e) => seen.set(e.rk, e));
            node.entries = [...seen.values()];
        }
        this.setKey(key, node);
    };
    getNode = (key) => this.getKey(key) ?? null;
    getEntries = (key) => this.getNode(key)?.entries ?? [];
    getTotal = (key) => this.getNode(key)?.total ?? 0;
    getPage = (key) => this.getNode(key)?.page ?? 1;

    clear = () => {
        this._data = {};
    };

    /**
     * Adds or updates an entry in a specific node.
     */
    addOrUpdate = (key, entry) => {
        try {
            const node = this.getNode(key);
            if (!node) {
                logService.logError(`${this._name}.addOrUpdate: Node not found: ${key}`);
                return;
            }

            const idx = node.entries.findIndex((e) => e.rk === entry.rk);
            if (idx !== -1) {
                node.entries[idx] = entry;
            } else {
                node.entries = [entry, ...node.entries];
            }
            node.total = node.entries.length; // CHANGED — always derive total from entries length
            this.setNode(key, node);
        } catch (error) {
            logService.logError(`${this._name}.addOrUpdate: ${error.message}`);
        }
    };

    /**
     * Updates the total entry count for a node.
     * Called after OnEntryNodesResolved to sync BE-provided totals.
     * @param {string} key - Node key, e.g. 'g1'
     * @param {number} total - Total entry count from BE
     */
    updateTotal = (key, total) => {
        try {
            const node = this.getNode(key);
            if (!node) return;
            node.total = total;
            this.setNode(key, node);
        } catch (error) {
            logService.logError(`${this._name}.updateTotal: ${error.message}`);
        }
    };

    /**
     * Removes a single entry from a specific node.
     */
    removeFromNode = (key, rk) => {
        try {
            const node = this.getNode(key);
            if (!node) return;
            node.entries = node.entries.filter((e) => e.rk !== rk);
            node.total = node.entries.length; // ADDED — keep total in sync
            this.setNode(key, node);
        } catch (error) {
            logService.logError(`${this._name}.removeFromNode: ${error.message}`);
        }
    };

    /**
     * Removes a single entry from all nodes — used when entry moves to a different node.
     */
    removeFromAllNodes = (rk) => {
        try {
            const keys = this.getKeys();
            if (!keys.length) {
                logService.logError(`${this._name}.removeFromAllNodes: node not found ${key}`);
                return;
            }

            keys.forEach((k) => this.removeFromNode(k, rk));
        } catch (error) {
            logService.logError(`${this._name}.removeFromAllNodes: ${error.message}`);
        }
    };
}

const groupNodeCache = new GroupNodeCache();
export default groupNodeCache;
